import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface MetricNumberProps {
  /** The pre-formatted display string, e.g. "$1.2M" or "31.9%". */
  value: string | number;
  /** Optional small suffix shown after the value (e.g. "/mo"). */
  suffix?: React.ReactNode;
  /** Maximum font size class (Tailwind), e.g. "text-3xl". Defaults to "text-2xl". */
  className?: string;
  /** Center horizontally inside the container. Default true. */
  center?: boolean;
}

/**
 * Renders a metric number that NEVER overflows its container.
 * - Truncates with ellipsis if absurdly long
 * - Auto-shrinks the font-size when the rendered text is wider than the container
 * - Uses tabular-nums for clean alignment
 *
 * Pair with one of the smart formatters in `@/lib/currency`
 * (formatCompactCurrency, formatCompactNumber, formatPercent).
 */
export function MetricNumber({
  value,
  suffix,
  className,
  center = true,
}: MetricNumberProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLSpanElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = innerRef.current;
    const wrap = containerRef.current;
    if (!el || !wrap) return;
    const fit = () => {
      // Reset to measure natural size
      el.style.transform = "scale(1)";
      const inner = el.scrollWidth;
      const outer = wrap.clientWidth;
      if (!inner || !outer) return;
      const next = Math.min(1, outer / inner);
      // Floor at 0.55 so it stays readable
      setScale(Math.max(0.55, next));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [value, suffix]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "w-full overflow-hidden",
        center ? "flex justify-center" : "flex justify-start",
      )}
    >
      <span
        ref={innerRef}
        className={cn(
          "font-medium tabular-nums whitespace-nowrap leading-tight inline-block origin-center",
          className ?? "text-2xl",
        )}
        style={{ transform: `scale(${scale})` }}
      >
        {value}
        {suffix ? <span className="text-sm font-normal text-muted-foreground ml-0.5">{suffix}</span> : null}
      </span>
    </div>
  );
}
